import { useCallback } from "react";

import { isAxiosError } from "axios";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Button } from "src/base-components/Button";
import {
  useCaseQueue,
  useCreateCaseQueue,
  useUpdateCaseQueue,
} from "src/cases/queries/queues";
import { CaseQueueConfiguration } from "src/cases/queues/CaseQueueConfiguration";
import { CaseQueueFormValues } from "src/cases/queues/types";
import { extractInitialFormValues } from "src/cases/queues/utils";
import { toastActions } from "src/design-system/Toast/utils";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { PageHeader } from "src/layout/PageHeader";
import {
  CaseConfigurationPageParams,
  getUrlToCaseQueuePage,
} from "src/router/urls";
import { logger } from "src/utils/logger";
import { getApiErrorDetail } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

const CaseQueueConfigurationContent: React.FC = () => {
  const {
    queue_id: queueId,
    wsId,
    orgId,
  } = useParamsDecode<CaseConfigurationPageParams>();
  const navigate = useNavigate();

  const { data: queue, isLoading: isLoadingQueue } = useCaseQueue(queueId);
  const { mutateAsync: createCaseQueue, isPending: isCreatingCaseQueue } =
    useCreateCaseQueue();
  const { mutateAsync: updateCaseQueue, isPending: isUpdatingCaseQueue } =
    useUpdateCaseQueue();

  const isEditing = !!queueId;

  const form = useForm<CaseQueueFormValues>({
    mode: "onBlur",
    values: extractInitialFormValues(queue),
  });

  const onSubmit = useCallback(
    async (values: CaseQueueFormValues) => {
      const isUpdating = !!queueId;

      try {
        if (isUpdating) {
          await updateCaseQueue({
            queueId,
            attributes: values.attributes,
            templateIds: values.templateIds,
            memberIds: values.memberIds,
            defaultTemplateVersion: values.defaultTemplateVersion,
          });

          toastActions.success({
            title: "Queue updated successfully",
          });
        } else {
          const queue = await createCaseQueue({
            attributes: values.attributes,
            templateIds: values.templateIds,
            memberIds: values.memberIds,
            defaultTemplateVersion: values.defaultTemplateVersion,
          });

          navigate(
            getUrlToCaseQueuePage({
              wsId,
              orgId,
              queueId: queue.data.id,
            }),
          );
        }
      } catch (error) {
        const operation = isUpdating ? "update" : "create";
        const activity = isUpdating ? "updating" : "creating";

        logger.error(`Failed to ${operation} queue`);

        if (isAxiosError(error)) {
          toastActions.failure({
            title: `Error ${activity} queue`,
            description: getApiErrorDetail(error, "An unknown error occured"),
          });
        } else {
          toastActions.failure({
            title: `Error ${activity} queue`,
            description: `Failed to ${operation} queue. Please try again later.`,
          });
        }
      }
    },
    [queueId, createCaseQueue, updateCaseQueue, wsId, orgId, navigate],
  );

  return (
    <div className="decideScrollbar flex h-full flex-col bg-gray-50">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <PageHeader
            backLink={getUrlToCaseQueuePage({
              orgId,
              wsId,
              queueId,
            })}
            sideContent={
              <Button
                dataLoc="save-queue-button"
                disabled={
                  !form.formState.isDirty ||
                  isCreatingCaseQueue ||
                  isUpdatingCaseQueue
                }
                htmlType="submit"
                loading={isCreatingCaseQueue || isUpdatingCaseQueue}
                size="sm"
              >
                {isEditing ? "Update Queue" : "Create Queue"}
              </Button>
            }
            title="Queue Configuration"
          />
          <div className="m-auto w-full min-w-[800px] max-w-[800px] gap-x-5 bg-gray-50 py-10">
            <CaseQueueConfiguration isLoading={isLoadingQueue} />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export const CaseQueueConfigurationPage: React.FC = () => {
  const { queue_id: queueId } = useParamsDecode<CaseConfigurationPageParams>();
  useDocumentTitle([queueId ? "Update Queue" : "Create Queue", "Case Queues"]);
  return <CaseQueueConfigurationContent />;
};
